/**
 * product.js — <nko-product-form> und <nko-gallery> der Produktseite.
 * Frueher assets/nko-product-form.js (seit M6 umbenannt). Ungebaute Quelle, direkt so
 * ausgeliefert; sections/product.liquid laedt sie als <script type="module"> (R5).
 *
 * Variantenwechsel ueber die Section Rendering API, Galerie samt Lightbox, Kauf ueber
 * cart/add.js mit Besetztzustand und Fehlermeldung, Bruecken zu Klaviyo und LoyaltyLion.
 * Die Kaufleiste am unteren Rand blendet assets/sticky-bar.js ein (eigene Datei, weil
 * der Konfigurator sie ohne <nko-product-form> braucht).
 *
 * ARCHITEKTUR
 * ---------------------------------------------------------------------------------------
 * Der Server besitzt den Zustand. Bei einer Auswahl schickt dieses Element die gewaehlten
 * Option-Value-IDs an die Section Rendering API und tauscht die zurueckgegebenen Regionen
 * ein. Es gibt keine Variantenmatrix im Dokument, keine Preisformatierung im JS, keine
 * clientseitige Verfuegbarkeitsberechnung und keine Label-Pflege — all das rendert Liquid,
 * also genau einmal und an genau einer Stelle.
 *
 *     GET <product.url>?option_values=<id,id,...>&section_id=<section.id>
 *
 * `option_values` ist der dokumentierte Weg fuer Produkte mit vielen Varianten: der Client
 * meldet die Auswahl, der Server loest die Variante auf. Dasselbe Muster nutzt
 * assets/variant-picker.js bereits fuer die Produktkarten.
 *
 * WAS DAS ERSETZT
 * ---------------------------------------------------------------------------------------
 * Legacy (assets/scripts.js:430-660, :1309-1762 und assets/app.js:340-434): ~12
 * jQuery-Handler pro Klick, ein BLOCKIERENDER fetch plus DOMParser, ein erneutes
 * JSON.parse der kompletten `product.variants`-Ausgabe (50-200 KB inline) und zwei
 * slick-Neuaufbauten mit adaptiveHeight.
 *
 * WAS INP SCHUETZT
 * ---------------------------------------------------------------------------------------
 *   1. `onIntent` waermt den Cache bei pointerover / focusin / pointerdown (auch ueber
 *      das <label>) samt Hero-Bild. Beim Klick ist die Antwort meist schon da.
 *   2. Anfragen werden pro Auswahl als Promise gecached — der Klick haengt sich an das
 *      laufende Vorladen, Zurueckwechseln kostet keinen Request.
 *   3. Getauscht wird erst, wenn das neue Hero-Bild dekodiert ist. Bis dahin bleibt die
 *      alte Ansicht stehen; ein langsames Netz zeigt alte Bilder statt leerer Kaesten.
 *   4. Ein Token verhindert, dass eine langsame fruehere Antwort eine spaetere ueberholt.
 *
 * DOM-VERTRAG — siehe docs/pdp-contract.md
 * ---------------------------------------------------------------------------------------
 *   <nko-product-form data-section-id data-product-id data-product-url>
 *     [data-nko-swap="buy|price|variants|gallery|sticky"]  Tauschregionen
 *     input / option[data-option-value-id]                        Optionscontrols
 *     script[type="application/json"][data-nko-variant]           aktuelle Variante, kompakt
 *
 * EREIGNISSE UND GLOBALE — Namen nie aendern (Abnehmer ausserhalb dieser Datei)
 * ---------------------------------------------------------------------------------------
 *   nko:variant:change   nach jedem Tausch, bubbles, detail { variant, sectionId, productId }
 *                        Hoerer: assets/bundles.js, accessories.js, gifts.js, face-scan-property.js
 *   nko:cart:add         nach 2xx von cart/add.js, detail { variant, productId, sections }
 *                        Hoerer: assets/cart-drawer.js, Klaviyo (snippets/scripts.liquid)
 *   change am [name="id"] nach jedem Tausch — Klaviyo Back-in-Stock liest dort die Variante
 *   window.theme.addToCart(form)          ruft assets/accessories.js (Popup-Kaufknopf)
 *   Shopify.refreshLoyaltyPoints(price)   LoyaltyLion, nach jedem Wechsel
 *   Shopify.FaceScan.handleAtc()          Kaufsperre von assets/face-scan-property.js
 *   theme.popupApp.open('accessories')    assets/popup.js
 *
 * AUF- UND ABBAU: jeder Hoerer haengt an EINEM AbortController pro Element
 * (`this.hoerer`), `disconnectedCallback` bricht ihn ab. Die Instanzen tauschen nie sich
 * selbst, nur Regionen darin; neu eingehaengte Elemente (Galerie nach dem Tausch, Section
 * im Editor) bauen sich in `connectedCallback` selbst auf.
 *
 * PRESERVE — Knoten, die den Tausch ueberleben MUESSEN
 * ---------------------------------------------------------------------------------------
 * Externe Apps halten Referenzen auf diese Knoten. Wuerden sie ersetzt, waeren die Apps
 * still tot — ohne Fehlermeldung. Sie werden aus dem lebenden DOM in das eingehende Markup
 * verpflanzt, bevor getauscht wird; nur ihr `class`-Attribut kommt vom Server, damit der
 * Zustand serverseitig bleibt.
 *
 * D4-MARKER
 *   `// TODO(D4):` bewusst nachgebauter Legacy-Fehler.  `// NOTE(D4):` bewusste Abweichung.
 */

/** Regionen, die eine Antwort ersetzen darf. Reihenfolge egal. */
const SWAP_REGIONS = ["gallery", "price", "variants", "buy", "sticky"];

/**
 * Knoten, die den Tausch ueberleben.
 *   #klaviyo-btn-container  Klaviyo bekommt die ID als nackten String
 *                           (`alternate_anchor`) und schreibt seinen Button hinein.
 *   .ruk_rating_snippet     reviews.io montiert die Sterne hier hinein.
 */
const PRESERVE = ["#klaviyo-btn-container", ".ruk_rating_snippet"];

/**
 * Knoten, die den Tausch MIT ihrem Zustand ueberleben — anders als PRESERVE
 * nimmt hier nichts die Klassen vom Server. Der Server rendert dieselben Knoten,
 * aber ohne das, was der Kunde darin getan hat.
 *   .face-scan-container   assets/face-scan.js haelt Referenzen darauf,
 *                          setzt `measured` und legt `properties[_choice]` /
 *                          `properties[_measured_mm]` hinein.
 *   .popup--face-scan-app  face-scan.js bindet die Knoepfe des Popups beim Start.
 * Ohne das: nach jedem Variantenwechsel ein totes Popup (Weiter/Auswahl/Kamera
 * ohne Handler) und eine verlorene Messung. Die Sperre bei ausverkaufter
 * Variante setzt die App selbst ueber `nko:variant:change`.
 *   .minicart__accessories die Zubehoer-Zeile der Sticky-Leiste („+ Etui …“).
 *                          assets/accessories.js schreibt sie
 *                          nur beim Speichern im Popup; der Server rendert sie
 *                          leer. Ohne das verschwand sie beim ersten Wechsel.
 */
const PRESERVE_STATE = [
  ".face-scan-container",
  ".popup--face-scan-app",
  ".minicart__accessories",
];

/**
 * iOS und iPadOS (jeder Browser dort ist WebKit). Dieselbe Abfrage wie im CSS der
 * Produktseite (`@supports (-webkit-touch-callout: none)`); macOS-Safari kennt die
 * Eigenschaft nicht. Gebraucht in `swap` (Fokus auf ein <select>).
 */
const IOS =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("-webkit-touch-callout", "none");

/**
 * Loest auf, sobald ein Bild gemalt werden kann — gedeckelt, damit die Oberflaeche
 * nie unbegrenzt auf das Netz wartet.
 *
 * MODULFUNKTION, nicht Methode: sie wird von <nko-product-form> (Hero vor dem
 * Regionstausch) UND von <nko-gallery> (grosse Fassung in der Lightbox) gebraucht.
 * Als Methode der Form-Klasse warf der Aufruf aus der Galerie
 * `TypeError: this.decode is not a function` — und weil er vor `showModal()` lag,
 * ging die Lightbox schlicht nicht auf.
 */
function decodeImage(img, timeout = 600) {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();
  const ready =
    typeof img.decode === "function"
      ? img.decode().catch(() => {})
      : new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        });
  return Promise.race([
    ready,
    new Promise((resolve) => setTimeout(resolve, timeout)),
  ]);
}

/** Ein Idle-Slot fuer alle nicht sichtbaren Abnehmer. Safari < 16.4 kennt kein rIC. */
function whenIdle(callback) {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(callback, { timeout: 500 });
  } else {
    setTimeout(callback, 0);
  }
}

/* ======================================================================================
 * <nko-product-form>
 * ==================================================================================== */

class NakedOpticsProductForm extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.getAttribute("data-section-id");
    this.productId = this.getAttribute("data-product-id");
    this.productUrl =
      this.getAttribute("data-product-url") || window.location.pathname;
    this.updateHistory = this.getAttribute("data-history") !== "false";
    // Der Cache ueberlebt ein Aus- und Einhaengen: dieselben Antworten gelten weiter.
    if (!this.cache) this.cache = new Map();

    this.hoerer?.abort();
    this.hoerer = new AbortController();
    const { signal } = this.hoerer;

    this.bindForm();

    this.addEventListener("change", this.onChange, { signal });
    this.addEventListener("pointerover", this.onIntent, {
      passive: true,
      signal,
    });
    this.addEventListener("pointerdown", this.onIntent, {
      passive: true,
      signal,
    });
    this.addEventListener("focusin", this.onIntent, { signal });
  }

  disconnectedCallback() {
    // Nimmt auch den submit-Hoerer am Formular mit (bindForm, gleiches Signal).
    this.hoerer?.abort();
    this.hoerer = null;
  }

  /**
   * Das Formular neu anbinden. Laeuft beim Start und nach jedem Tausch der Region "buy",
   * weil dabei ein neues <form>-Element entsteht.
   *
   * Bewusst VOR allem anderen: `theme.addToCart` muss auch dann existieren, wenn sonst
   * etwas schiefgeht — assets/accessories.js (Popup-Kaufknopf) ruft es auf und
   * wuerde sonst mit einem TypeError abbrechen.
   */
  bindForm() {
    // ACHTUNG bei `this.form.id`: das liefert NICHT die ID, sondern das <select
    // name="id"> darin. Formularelemente werden von ihren benannten Feldern
    // ueberschattet — und das Master-Select heisst nun mal `id`, weil `cart/add.js`
    // das so erwartet. Wer die ID braucht: `getAttribute('id')`.
    this.form = this.querySelector("form");
    if (this.form && this.hoerer)
      this.form.addEventListener("submit", this.onSubmit, {
        signal: this.hoerer.signal,
      });

    window.theme = window.theme || {};
    window.theme.addToCart = (form) => this.addToCart(form || this.form);
  }

  /* ---- Auswahl -> Anfrage ---- */

  get optionInputs() {
    return Array.from(this.querySelectorAll("[data-option-value-id]"));
  }

  /** Die aktuell gewaehlten Option-Value-IDs, in Markup-Reihenfolge. */
  selectedOptionValueIds(overrideNode) {
    const ids = [];
    const overridePosition = overrideNode
      ? this.optionInputs.indexOf(overrideNode)
      : -1;

    for (const node of this.optionInputs) {
      const chosen =
        node.tagName === "OPTION"
          ? node.selected
          : node.type === "radio" || node.type === "checkbox"
            ? node.checked
            : true;

      if (node === overrideNode) {
        ids.push(node.dataset.optionValueId);
      } else if (chosen && !this.sharesGroupWith(node, overrideNode)) {
        ids.push(node.dataset.optionValueId);
      }
    }
    return ids.filter(Boolean);
  }

  /**
   * Zwei Radios derselben Gruppe schliessen sich aus — `name` ist das Kriterium.
   * Bei <option> ist es das gemeinsame <select>: Firefox meldet `pointerover` auf
   * den Eintraegen der offenen Liste, und ohne diese Zeile landete die bisher
   * gewaehlte Option neben der ueberfahrenen im Vorab-Request.
   */
  sharesGroupWith(node, other) {
    if (!other || node === other) return false;
    if (node.tagName === "OPTION")
      return node.parentElement === other.parentElement;
    return Boolean(node.name) && node.name === other.name;
  }

  sectionUrl(optionValueIds) {
    const params = new URLSearchParams();
    if (optionValueIds.length)
      params.set("option_values", optionValueIds.join(","));
    params.set("section_id", this.sectionId);
    return `${this.productUrl}?${params}`;
  }

  /**
   * Holt (oder wiederverwendet) das Section-Markup fuer eine Auswahl. Wirft nie,
   * loest zu `null` auf, wenn keine Antwort kam.
   *
   * Gecacht wird das PROMISE, nicht erst das fertige HTML. Beim Klick feuern
   * `focusin` (Vorladen) und `change` (Wechsel) fast gleichzeitig; mit einem
   * Cache nur fuer fertige Antworten gingen dieselben 287 KB zweimal ueber die
   * Leitung, und der Wechsel wartete auf die zweite. Jetzt haengt sich der
   * Wechsel an die laufende Anfrage.
   *
   * Deshalb auch kein AbortController mehr: eine geteilte Anfrage darf keiner
   * der Wartenden abbrechen. Dass eine langsame fruehere Antwort keine spaetere
   * ueberholt, sichert das Token in `wechsle`. Fehlschlaege fliegen aus dem
   * Cache, damit der naechste Versuch neu fragt.
   */
  fetchSection(optionValueIds) {
    const key = optionValueIds.join(",");
    if (this.cache.has(key)) return this.cache.get(key);

    const anfrage = fetch(this.sectionUrl(optionValueIds))
      .then((response) => (response.ok ? response.text() : null))
      .catch(() => null); // offline — die aktuelle Ansicht bleibt einfach stehen
    this.cache.set(key, anfrage);
    anfrage.then((html) => {
      if (html === null && this.cache.get(key) === anfrage)
        this.cache.delete(key);
    });
    return anfrage;
  }

  /* ---- Interaktion ---- */

  onChange = (event) => {
    const node = event.target;
    if (!node || !node.matches) return;
    // Radios tragen die Kennung selbst. Beim Dropdown (Metafeld
    // `custom.option_selection_types`) feuert `change` am <select>, die Kennung
    // steht an dessen <option>s. Andere Selects im Formular (Geschenk, Bundle)
    // haben keine solchen <option>s und bleiben aussen vor.
    const optionControl =
      node.matches("[data-option-value-id]") ||
      (node.tagName === "SELECT" &&
        node.querySelector("[data-option-value-id]"));
    if (!optionControl) return;
    this.apply(this.selectedOptionValueIds());
  };

  /**
   * Cache bei Absicht waermen, nicht erst bei Auswahl.
   *
   * Ohne das laege die Netzwerkzeit voll auf dem Interaktionspfad — genau der Vorwurf an
   * das Legacy (Befund I1). Kostet nichts ohne Interaktion; eine falsche Wette kostet
   * genau einen Request.
   */
  onIntent = (event) => {
    const control = this.controlFuer(event.target);
    if (!control) return;

    const ids = this.selectedOptionValueIds(control);
    const key = ids.join(",");
    if (!ids.length || this.cache.has(key) || key === this.currentKey) return;

    // Das Heldbild gleich mit vorwaermen, sobald das Markup da ist — dann trifft
    // der Klick auch das Bild im HTTP-Cache. Im Leerlauf, weil das Parsen der
    // Antwort den Hauptthread ein paar Millisekunden kostet.
    this.fetchSection(ids).then((html) => {
      if (!html) return;
      whenIdle(() =>
        this.preloadHero(new DOMParser().parseFromString(html, "text/html")),
      );
    });
  };

  /**
   * Das Optionscontrol zu einem Ereignisziel. Der Zeiger trifft praktisch nie das
   * Radio selbst (visuell versteckt), sondern sein <label> — ein GESCHWISTER, kein
   * Vorfahr. `closest()` allein fand deshalb nie etwas, und das Vorladen bei
   * Hover/pointerdown lief kein einziges Mal.
   */
  controlFuer(ziel) {
    if (!ziel || !ziel.closest) return null;
    const direkt = ziel.closest("[data-option-value-id]");
    if (direkt) return direkt;
    const label = ziel.closest("label");
    const control =
      label &&
      (label.control ||
        (label.htmlFor && document.getElementById(label.htmlFor)));
    return control &&
      control.matches("[data-option-value-id]") &&
      this.contains(control)
      ? control
      : null;
  }

  /* ---- Tausch ---- */

  /**
   * Der laufende Tausch steht in `this.pending`, damit der Kauf darauf warten kann
   * (`warteAufTausch`). Bis die Antwort da ist, zeigen Radios und Face-Scan schon die
   * neue Auswahl, das Formular traegt aber noch die alte Variante — wer genau dann
   * kaufte, bekam die alte. Beim Face-Scan heisst das: Messwerte fuer Groesse M,
   * Variante S. Loest zu `false` auf, wenn keine Antwort kam.
   *
   * Ist der Lauf vorbei, wird `pending` wieder leer. Vorher blieb dort ein
   * gescheiterter Lauf stehen, und JEDER weitere Kaufversuch wartete auf ihn,
   * bekam `false` und zeigte „Bitte versuche es erneut“ — ohne je neu zu
   * fragen (gemessen mit 503 auf der Section-Anfrage: 3 Klicks, 0 cart/add).
   */
  apply(optionValueIds) {
    this.gewuenscht = optionValueIds;
    const lauf = this.wechsle(optionValueIds);
    this.pending = lauf;
    const abraeumen = () => {
      if (this.pending === lauf) this.pending = null;
    };
    lauf.then(abraeumen, abraeumen);
    return lauf;
  }

  async wechsle(optionValueIds) {
    const token = (this.token = (this.token || 0) + 1);

    const html = await this.fetchSection(optionValueIds);
    // Ueberholt: ein spaeterer Aufruf haelt `this.pending`, auf den wird gewartet.
    if (token !== this.token) return true;
    if (!html) {
      // Radios und Dropdowns zeigen schon die neue Auswahl, Formular und Preis
      // noch die alte. Zurueck auf den Stand, den der Server gerendert hat
      // (`checked`/`selected` im Markup), damit Anzeige und Kauf dieselbe
      // Variante meinen.
      for (const node of this.optionInputs) {
        if (node.tagName === "OPTION") node.selected = node.defaultSelected;
        else if (node.type === "radio" || node.type === "checkbox")
          node.checked = node.defaultChecked;
      }
      return false;
    }

    const incoming = new DOMParser().parseFromString(html, "text/html");

    // Das neue Hero-Bild laden, BEVOR das DOM angefasst wird. Bis dahin bleibt die alte
    // Ansicht stehen — im schlechtesten Fall haelt die vorige Variante kurz, nie ein
    // leerer Kasten.
    //
    // ACHTUNG: nicht das <img> aus der Antwort dekodieren. Ein DOMParser-Dokument ist
    // INERT — es hat keinen Browsing-Kontext, seine Bilder laden nie, `decode()` wuerde
    // also immer in den Timeout laufen und ein ungeladenes Bild einsetzen (gemessen: 47
    // leere Frames, rund 780 ms). Stattdessen dieselbe Quelle ueber ein Image() im
    // lebenden Dokument holen; der Tausch trifft danach den HTTP-Cache.
    await this.preloadHero(incoming);
    if (token !== this.token) return true;

    this.swap(incoming);
    this.currentKey = optionValueIds.join(",");

    const variant = this.readVariant();
    this.updateHistoryState(variant);

    // KLAVIYO BACK-IN-STOCK hoert dokumentweit auf `change` und liest in dem
    // Moment `[name="id"]` aus. Im Legacy setzte der jQuery-Handler am Radio das
    // Master-Select SYNCHRON um, bevor das Ereignis bei Klaviyo ankam. Hier kommt
    // die neue Variante erst mit der Antwort — Klaviyo las die alte und lag immer
    // einen Wechsel zurueck: kein "Benachrichtige mich" auf der ausverkauften
    // Variante, dafuer auf der verfuegbaren danach. Gemessen auf
    // /products/the-cruzer-evo-mitten (Bronze/7 -> Schwarz/7 -> Bronze/7).
    //
    // Deshalb nach dem Tausch ein `change` am neuen Master-Select — so wie es
    // Shopifys Referenz-Theme fuer Apps tut. Ohne Folgen im eigenen Code:
    // `onChange` verlangt `data-option-value-id`, die Bundle-Hoerer filtern auf
    // `.product__combo--bundle`.
    const idFeld = this.form && this.form.querySelector('[name="id"]');
    if (idFeld) idFeld.dispatchEvent(new Event("change", { bubbles: true }));

    this.dispatchEvent(
      new CustomEvent("nko:variant:change", {
        bubbles: true,
        composed: true,
        detail: {
          variant,
          sectionId: this.sectionId,
          productId: this.productId,
        },
      }),
    );

    whenIdle(() => this.notifySubscribers(variant));
    return true;
  }

  /**
   * Auf den jeweils LETZTEN Tausch warten — startet waehrenddessen ein neuer,
   * auch auf den. `true` ohne laufenden Tausch.
   */
  async warteAufTausch() {
    for (;;) {
      const lauf = this.pending;
      if (!lauf) return true;
      const ok = await lauf;
      // `apply` raeumt `pending` nach dem Lauf ab — steht dort jetzt ein
      // ANDERER Lauf, ist inzwischen ein neuer gestartet: auf den warten.
      if (!this.pending || this.pending === lauf) return ok;
    }
  }

  /** Jede markierte Region durch ihr Gegenstueck aus der Antwort ersetzen. */
  swap(incoming) {
    for (const region of SWAP_REGIONS) {
      const selector = `[data-nko-swap="${region}"]`;
      const next = incoming.querySelector(selector);
      // Galerie und Sticky-Leiste liegen ausserhalb dieses Elements — dokumentweit suchen.
      // Die Sticky-Leiste wird KOMPLETT getauscht, nicht nur ihr Preis: sie traegt
      // `.js-status-avl` und muss bei ausverkaufter Variante mit verschwinden.
      const current =
        this.querySelector(selector) || document.querySelector(selector);
      if (!next || !current) continue;

      // Einen OFFENEN Dialog schliessen, solange er noch im Dokument haengt.
      // Die Lightbox liegt in <nko-gallery>; wird die Region ersetzt, verschwindet
      // sie mit — dann laufen die Close-Schritte des Browsers NICHT, der Fokus
      // landet auf <body>, und in manchen Engines bleibt das Dokument als
      // "blocked by modal" zurueck, ohne dass noch ein Dialog zum Schliessen da
      // waere. Erreichbar, weil `apply()` zwei await-Punkte hat, waehrend derer
      // die alte Ansicht bedienbar bleibt: Swatch klicken, dann aufs Foto.
      const offenerDialog =
        current.querySelector && current.querySelector("dialog[open]");
      if (offenerDialog) offenerDialog.close();

      // Den Fokus merken, BEVOR die Region verschwindet. Sonst faellt er beim
      // Ersetzen auf <body>, und wer die Farbe mit den Pfeiltasten waehlt,
      // steht wieder am Dokumentanfang — bei acht Farben pro Produkt achtmal.
      // Nachgemessen: Radio fokussiert, geklickt, nach 2,5 s ist
      // `document.activeElement === document.body`.
      //
      // Die `id` ueberlebt den Tausch: der Server rendert dieselbe Region mit
      // denselben Kennungen neu, nur mit anderem `checked`. Genau das macht
      // die Rueckgabe hier ueberhaupt moeglich — nachgeprueft, ein Radio mit
      // derselben id existiert danach und traegt `checked`.
      //
      // Dieselben drei Zeilen wie unten fuer den Zoom-Link der Lightbox; dort
      // wurden sie von Anfang an gezogen, fuer die Optionscontrols nicht.
      const fokusKnoten =
        current.contains && current.contains(document.activeElement)
          ? document.activeElement
          : null;
      const fokusVorher = fokusKnoten ? fokusKnoten.id : null;

      this.transplantPreserved(current, next);
      current.replaceWith(next);

      // Ein Knoten aus PRESERVE / PRESERVE_STATE ist derselbe geblieben, hat den
      // Fokus beim Umhaengen aber verloren — z. B. "Gesichtsbreite ermitteln"
      // nach einer Messung per Tastatur (der Knopf hat keine id).
      if (fokusKnoten && fokusKnoten.isConnected) {
        if (document.activeElement !== fokusKnoten)
          fokusKnoten.focus({ preventScroll: true });
      } else if (fokusVorher) {
        const zurueck = next.querySelector(`[id="${CSS.escape(fokusVorher)}"]`);
        // AUSNAHME iOS/iPadOS: dort oeffnet `focus()` auf einem <select> dessen
        // Auswahlmenue. Der Kunde hatte gerade eine Groesse gewaehlt, das Menue
        // ging zu — und mit der Antwort des Servers wieder auf, ueber Kurztext
        // und Kaufknopf, mit dem Haken beim neuen Wert (the-cruzer-evo-mitten,
        // the-draft). Erst ein Tipp daneben schloss es, und der war verbraucht.
        // Der Fokus geht dort stattdessen auf die Gruppe um das Feld: fuer die
        // Beruehrung folgenlos, aber VoiceOver und eine angeschlossene
        // Tastatur bleiben an der Stelle, statt auf <body> und damit an den
        // Seitenanfang zu fallen. Radios und alle anderen Browser wie bisher.
        if (zurueck && IOS && zurueck.tagName === "SELECT") {
          const gruppe = zurueck.closest(".group") || zurueck.parentElement;
          if (gruppe) {
            if (!gruppe.hasAttribute("tabindex"))
              gruppe.setAttribute("tabindex", "-1");
            gruppe.focus({ preventScroll: true });
          }
        } else if (zurueck) {
          zurueck.focus({ preventScroll: true });
        }
      }

      // War die Lightbox offen, hat `close()` den Fokus auf den Zoom-Link
      // zurueckgegeben — der aber gerade mit ersetzt wurde, sodass er auf <body>
      // faellt. Dann auf das Gegenstueck in der neuen Region setzen, damit ein
      // Tastaturnutzer nicht am Seitenanfang wieder anfangen muss.
      if (offenerDialog) {
        const ersatz =
          next.querySelector && next.querySelector("[data-nko-zoom]");
        if (ersatz) ersatz.focus({ preventScroll: true });
      }
    }

    // Die Region "buy" bringt ein neues <form> mit — Listener und theme.addToCart neu setzen.
    this.bindForm();
  }

  /**
   * Lebende Knoten, auf die externe Apps Referenzen halten, in das eingehende Markup
   * verpflanzen. Nur das `class`-Attribut kommt vom Server, damit der sichtbare Zustand
   * serverseitig bleibt (z. B. `hide` am Klaviyo-Container bei verfuegbarer Variante).
   */
  transplantPreserved(current, next) {
    for (const selector of PRESERVE) {
      const live = current.querySelector(selector);
      const fresh = next.querySelector(selector);
      if (!live || !fresh) continue;
      live.className = fresh.className;
      fresh.replaceWith(live);
    }

    for (const selector of PRESERVE_STATE) {
      const fresh = next.querySelector(selector);
      if (!fresh) continue;
      const live =
        current.querySelector(selector) || document.querySelector(selector);
      if (!live) continue;
      // Hat ein Popup-System den Knoten schon woandershin verschoben, bleibt er
      // dort — die frische Kopie faellt weg, sonst gaebe es ihn zweimal.
      if (current.contains(live)) fresh.replaceWith(live);
      else fresh.remove();
    }
  }

  /**
   * Holt das Hero-Bild der Antwort ueber ein Image() im lebenden Dokument.
   * `srcset` und `sizes` werden mitgenommen, damit der Browser denselben Kandidaten
   * waehlt wie spaeter im echten <img> — sonst laedt er zweimal.
   */
  preloadHero(incoming) {
    // `[data-nko-swap="gallery"] img` waere FALSCH: das erste Bild in der Galerie ist ein
    // 40px-Thumbnail, weil die Thumbnail-Leiste im Markup vor der Vorschau steht. Damit
    // wurde ein Miniaturbild vorgeladen und das echte Hero blieb kalt (gemessen: 44 leere
    // Frames). Es muss das erste SLIDE sein.
    const hero = incoming.querySelector("[data-nko-slide] img");
    if (!hero) return Promise.resolve();

    const preloader = new Image();
    preloader.fetchPriority = "high";
    const sizes = hero.getAttribute("sizes");
    const srcset = hero.getAttribute("srcset");
    if (sizes) preloader.sizes = sizes;
    if (srcset) preloader.srcset = srcset;
    preloader.src = hero.currentSrc || hero.getAttribute("src") || "";

    // Das eingehende <img> gleich auf eager stellen, damit es nach dem Einsetzen nicht
    // erst auf den Lazy-Loader wartet.
    hero.setAttribute("loading", "eager");
    hero.setAttribute("fetchpriority", "high");

    return decodeImage(preloader);
  }

  /* ---- Bruecken nach aussen ---- */

  /** Die aktuelle Variante, kompakt — gerendert von blocks/product-form.liquid. */
  readVariant() {
    const node = this.querySelector(
      'script[type="application/json"][data-nko-variant]',
    );
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  updateHistoryState(variant) {
    if (
      !this.updateHistory ||
      !variant ||
      !window.history ||
      !window.history.replaceState
    )
      return;
    const url = new URL(window.location.href);
    url.searchParams.set("variant", String(variant.id));
    window.history.replaceState({ path: url.toString() }, "", url.toString());
  }

  notifySubscribers(variant) {
    if (!variant) return;

    // Die Punkte rendert Liquid inzwischen serverseitig mit. Der Aufruf bleibt, weil
    // LoyaltyLion die Funktion von aussen erwartet und andere Stellen sie ebenfalls rufen.
    const shopify = window.Shopify;
    if (shopify && typeof shopify.refreshLoyaltyPoints === "function") {
      shopify.refreshLoyaltyPoints(variant.price);
    }

    /* Hier stand eine Bruecke, die zusaetzlich ein jQuery-Ereignis
       `variantChange` auf `.product-section` ausloeste. Alle drei Abnehmer der
       neuen Produktseite hoeren inzwischen auf `nko:variant:change`:
         snippets/scripts.liquid        — der LoyaltyLion-Hoerer war ein
                                          Zwilling zu refreshLoyaltyPoints und
                                          ist ersatzlos entfallen
         assets/accessories.js
         assets/bundles.js
       Der vierte Abnehmer war assets/face-scan-app.js (seit M10b
       face-scan-property.js). Die Annahme hier war
       falsch: das Skript gehoert NICHT zu einer eigenen Vorlage, sondern
       kommt ueber blocks/product-form.liquid auf diese Seite, sobald ein
       Produkt das Metafeld `custom.face_width_formula` hat. Es hoert jetzt
       ebenfalls auf `nko:variant:change`. Derzeit hat kein Produkt das
       Metafeld, die Zeile war also schlafend — aber sie haette geworfen.
       Damit ist die letzte jQuery-Abhaengigkeit der neuen Produktseite weg. */
  }

  /* ---- Add to cart ---- */

  /**
   * ALLE Kaufknoepfe dieses Formulars: der im Formular und der in der
   * Sticky-Leiste. Letzterer liegt ausserhalb dieses Elements und haengt nur per
   * `form="AddToCartForm-…"` am Formular — `button.form` loest genau das auf.
   * Unter 576px ist der Knopf im Formular ausgeblendet; bekam nur er den
   * Besetztzustand, sah der angetippte Knopf unveraendert aus.
   */
  get kaufKnoepfe() {
    return Array.from(document.querySelectorAll("[data-nko-atc]")).filter(
      (knopf) => knopf.form === this.form,
    );
  }

  onSubmit = (event) => {
    // Eine Wache (Geschenke, Bundles) hat den Kauf schon abgefangen und ihr
    // Popup geoeffnet. Sie haengt dokumentweit in der Capture-Phase, laeuft also
    // vor diesem Hoerer; `stopPropagation` dort haelt Hoerer am Formular selbst
    // aber nicht auf — ohne diese Zeile kaufte es trotzdem. Deshalb VOR dem
    // eigenen `preventDefault` lesen.
    const abgefangen = event.defaultPrevented;
    event.preventDefault();
    if (abgefangen) return;
    this.letzterKnopf = event.submitter || null;

    this.addToCart(event.currentTarget);
  };

  async addToCart(form) {
    if (!form) return;

    /* LAUFSPERRE. Bis hierher gab es keine, und `setLoading` war wirkungslos
       (siehe dort) — waehrend eines langsamen `cart/add.js` erzeugte jeder
       weitere Klick eine WEITERE Position. Der klassische Doppelbestellfehler.

       Die Sperre steht vor den Wachen fuer Face-Scan und Zubehoer, damit ein
       zweiter Klick auch dann folgenlos bleibt, wenn die erste Anfrage noch in
       einer dieser Pruefungen haengt. */
    if (this.kaufLaeuft) return;
    this.kaufLaeuft = true;
    try {
      await this.addToCartIntern(form);
    } finally {
      this.kaufLaeuft = false;
    }
  }

  async addToCartIntern(form) {
    // ERST DIE AUSWAHL, DANN DER KAUF. Laeuft noch ein Variantenwechsel, traegt
    // das Formular die alte Variante — bis zur Antwort warten (siehe `apply`).
    // Kam keine, zeigt der Kaufbereich den Fehler, statt den falschen Artikel zu
    // kaufen. Danach ist das uebergebene Formular womoeglich ersetzt.
    if (this.pending) {
      this.setLoading(true);
      let tauschOk = await this.warteAufTausch();
      // Einmal nachfassen, bevor der Kunde einen Fehler sieht: ein einzelner
      // 429/503 der Section-Anfrage soll den Kauf nicht kosten.
      if (!tauschOk && this.gewuenscht)
        tauschOk = await this.apply(this.gewuenscht);
      this.setLoading(false);
      if (!tauschOk) {
        this.zeigeFehler(this.getAttribute("data-label-cart-error"));
        return;
      }
      if (!form.isConnected) form = this.form;
    }

    // Face-Scan: blockiert den Kauf, solange nicht gemessen wurde, und liefert die
    // Line-Item-Properties `_choice` / `_measured_mm`.
    //
    // Fehlt die App (`handleAtc`), bleibt der Kauf ebenfalls GESPERRT —
    // Entscheidung D-FACESCAN-LAZY vom 29.09.2026, sie ersetzt Ledger C05
    // („durchlassen, aber mit `_measurement_missing` markieren“). Ohne App kann
    // niemand gemessen haben; eine massgefertigte Brille ohne Messung soll
    // nicht in den Warenkorb. Seit MediaPipe und die Messung (assets/face-scan.js)
    // erst beim Scan laden, tritt der Fall nur noch auf, wenn
    // assets/face-scan-property.js selbst nicht ankam oder face-scan.js nicht
    // nachgeladen werden konnte (dann entfernt face-scan-property.js
    // `handleAtc`). „Noch nicht ausgewertet“ gibt es
    // hier nicht: es steht im Formular-Block VOR dieser Datei (Ende von
    // sections/product.liquid, <script> fuer product.js), und Module laufen in
    // Dokumentreihenfolge —
    // ohne `handleAtc` ist die App also gescheitert. Dann der Ladefehler mit
    // „Erneut versuchen“, nicht „Bitte ermittle zuerst deine Gesichtsbreite“:
    // messen geht ja gerade nicht. Wirft `handleAtc`, gilt dasselbe.
    try {
      if (document.querySelector("#FaceWidthCheckbox")) {
        const faceScan = window.Shopify && window.Shopify.FaceScan;
        if (faceScan && typeof faceScan.handleAtc === "function") {
          if (faceScan.handleAtc() === false) return;
        } else {
          this.zeigeLadefehler();
          return;
        }
      }
    } catch (error) {
      this.zeigeLadefehler();
      return;
    }

    // Accessories: erzwingt eine Auswahl, bevor gekauft werden kann.
    try {
      const accessories = document.querySelector(
        "accessories-component, nko-accessories",
      );
      if (
        accessories &&
        typeof accessories.needsSelection === "function" &&
        accessories.needsSelection()
      ) {
        if (document.querySelector(".popup--accessories")) {
          const popup = window.theme && window.theme.popupApp;
          if (popup && typeof popup.open === "function")
            popup.open("accessories");
        }
        return;
      }
    } catch (error) {
      /* siehe oben */
    }

    this.setLoading(true);
    try {
      const root =
        (window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        "/";
      const daten = new FormData(form);

      // BUNDLED SECTION RENDERING — Vertrag mit assets/cart-drawer.js: die
      // Schublade rendert ihren Inhalt aus `detail.sections` des Ereignisses
      // unten, ohne zweite GET-Anfrage. Fehlt die Schublade, bleibt der
      // Aufruf wie gehabt; fehlt `detail.sections`, holt sie selbst nach.
      const schublade = document.querySelector("nko-cart-drawer");
      const schubladenSektion = schublade && schublade.dataset.section;
      if (schubladenSektion) {
        daten.append("sections", schubladenSektion);
        daten.append("sections_url", root);
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        body: daten,
      });
      if (!response.ok) {
        /* Bei 422 nennt Shopify den Grund („Du kannst nur 2 davon …“). Den
           zeigen wir statt des allgemeinen „erneut versuchen“, das bei einer
           Bestands- oder Mengengrenze nicht hilft — LIVE zeigte ihn ebenfalls.
           Er landet als Text im Feld, nie als HTML. */
        let grund = null;
        try {
          const antwort = await response.json();
          grund =
            response.status === 422
              ? antwort.description || antwort.message || null
              : null;
        } catch (error) {
          /* keine JSON-Antwort: allgemeiner Text */
        }
        const fehler = new Error(`cart/add.js antwortete ${response.status}`);
        fehler.grund = typeof grund === "string" ? grund : null;
        throw fehler;
      }
      let sections = null;
      try {
        const antwort = await response.json();
        sections = (antwort && antwort.sections) || null;
      } catch (error) {
        /* ohne Sections holt die Schublade selbst nach */
      }
      this.zeigeFehler(null);

      /* Ledger B01: Klaviyo haengte bisher am KLICK auf den Kaufknopf. Das ist
         die falsche Stelle — die Face-Scan- und die Bundle-Wache brechen den
         Kauf regelmaessig ab, und ein Ereignis fuer einen Kauf, der nie
         stattfand, ist schlimmer als keines. Dieses Signal feuert erst, wenn
         cart/add.js wirklich mit 2xx geantwortet hat. Abnehmer:
         snippets/scripts.liquid (Klaviyo) und assets/cart-drawer.js. */
      this.dispatchEvent(
        new CustomEvent("nko:cart:add", {
          bubbles: true,
          composed: true,
          detail: {
            variant: this.readVariant(),
            productId: this.productId,
            sections,
          },
        }),
      );

      /* ================================================================
         WER DIE SCHUBLADE OEFFNET

         Seit dem Warenkorb-Umbau macht das die Schublade SELBST: sie hoert
         auf `nko:cart:add` (assets/cart-drawer.js), laedt den Warenkorb
         nach und faehrt auf. Hier ist deshalb nichts mehr zu tun.

         HIER STAND EIN RUECKFALL, DER DEN KAUF ZERSTOERT HAT:

             else { setTimeout(() => window.location.replace('/cart'), 1000) }

         Er stammt aus der Zeit, als HulkApps die Schublade stellte, und
         sollte greifen, wenn die App fehlt. Seit die App abgeschaltet ist,
         ist `window.HSSLIDECART` immer undefiniert — der Zweig lief also
         IMMER. Ablauf aus Kundensicht: Artikel landet im Warenkorb, die
         Schublade faehrt auf, und eine Sekunde spaeter springt die Seite auf
         die Startseite. Denn `/cart` leitete dorthin um
         (`cart_redirect_home`, entschieden am 2026-08-31; seit D-CART-PAGE
         leitet layout/theme.liquid auf die Startseite mit offener
         Schublade um, die Einstellung gibt es nicht mehr).

         Der Rueckfall ist ersatzlos gestrichen. Ein `location.replace('/cart')`
         kann in diesem Shop NIE das Richtige tun, solange die Warenkorbseite
         umleitet — es endet zwangslaeufig auf der Startseite, weg vom
         Produkt.

         Die beiden HulkApps-Zweige davor (`Hs_CartDrawer`/`HsCartDrawer`,
         `updateSlideCart()` + `getSlideCart()`) sind mit D-HULK ebenfalls
         entfallen.
         ================================================================ */
    } catch (error) {
      /* Ledger C04, freigegeben: statt die Schublade zu oeffnen, sagen wir dem
         Kunden, dass es nicht geklappt hat. Vorher sah er eine geoeffnete
         Schublade ohne sein Produkt und keinen Hinweis, warum. */
      this.zeigeFehler(
        (error && error.grund) || this.getAttribute("data-label-cart-error"),
      );
    } finally {
      this.setLoading(false);
    }
  }

  /* Ledger C04. Plaetze dafuer stehen in blocks/product-form.liquid und in der
     Sticky-Leiste (sections/product.liquid), beide `hidden`, solange nichts
     anliegt. Die Meldung kommt an den Platz des angetippten Knopfs: unter 576px
     ist der Knopf im Formular ausgeblendet, und dessen Feld stand dann 876px
     ueber der Leiste, ausserhalb des Sichtfelds. Nur EIN Feld wird befuellt,
     sonst liest ein Screenreader die Meldung zweimal (beide `role="alert"`).
     `null` raeumt alle ab — sonst bliebe die Meldung nach einem geglueckten
     zweiten Versuch stehen. */
  zeigeFehler(text) {
    const felder = Array.from(
      document.querySelectorAll("[data-nko-cart-error]"),
    );
    for (const feld of felder) {
      feld.hidden = true;
      feld.textContent = "";
    }
    if (!text || !felder.length) return;

    const knopf = this.letzterKnopf;
    const leiste =
      knopf && knopf.isConnected && knopf.closest('[data-nko-swap="sticky"]');
    const feld =
      (leiste && leiste.querySelector("[data-nko-cart-error]")) ||
      felder.find(
        (kandidat) => !kandidat.closest('[data-nko-swap="sticky"]'),
      ) ||
      felder[0];
    feld.textContent = text;
    feld.hidden = false;
  }

  /* Face-Scan ohne App (siehe `addToCartIntern`): den Ladefehler aus
     snippets/face-scan-property.liquid zeigen. Meist steht er seit dem Laden
     (`onerror` am Skript); hier kommt der Fall dazu, dass die App ankam, aber
     scheiterte. Der Messhinweis geht weg, sonst stuenden zwei Gruende da.
     Fokus auf „Erneut versuchen“ wie im Popup, wenn MediaPipe nicht laedt.
     Den Abstand zum fixierten Kopf haelt `scroll-margin-top` im CSS der
     Produktseite. */
  zeigeLadefehler() {
    const hinweis = document.querySelector("[data-face-scan-load-error]");
    if (!hinweis) return;
    const messHinweis = document.querySelector("[data-face-scan-required]");
    if (messHinweis) messHinweis.hidden = true;
    hinweis.hidden = false;
    hinweis.scrollIntoView({ behavior: "smooth", block: "nearest" });
    const knopf = hinweis.querySelector("button");
    if (knopf) knopf.focus({ preventScroll: true });
  }

  /* BESETZTZUSTAND DES KAUFKNOPFS.

     Hier standen zwei Klassenumschaltungen, die auf dieser Seite BEIDE nichts
     taten:

       `js-loader-show` am <body>  Die einzige Regel dazu stand im Blatt der
                                   Kollektion (`.js-loader-show .loader`),
                                   und das laedt auf der Produktseite nicht.
                                   Ausserdem gibt es hier gar kein
                                   `.loader`-Element — collection.js legt es
                                   selbst an, diese Datei nie.
       `is-loading` am Knopf       NULL Regeln, in keinem einzigen Blatt.

     Der Knopf sah also waehrend der ganzen Anfrage unveraendert aus.

     Jetzt nur `aria-busy`, KEIN `disabled`. Die Laufsperre in `addToCart`
     verhindert den Doppelkauf allein. Ein `disabled` faerbte den Knopf grau
     (`.btn[disabled]` in base.css) und schaltete die
     `.btn--gradient:not(:disabled)`-Regeln ab — LIVE bleibt er lila und zeigt
     einen Ladekreis. Den zeichnet das CSS der Produktseite ueber
     `[aria-busy="true"]`, auf BEIDEN Kaufknoepfen: der in der Sticky-Leiste ist
     unter 576px der einzige sichtbare. */
  setLoading(loading) {
    for (const button of this.kaufKnoepfe) {
      button.setAttribute("aria-busy", loading ? "true" : "false");
    }
  }
}

/* ======================================================================================
 * <nko-gallery> — reine Progressive Enhancement
 *
 * Das Markup ist eine CSS-Scroll-Snap-Schiene, die ohne JS funktioniert: Thumbnails und
 * Punkte sind echte In-Page-Anker, das Zoom-Ziel ist ein Link. Diese Klasse ergaenzt
 * weiches Scrollen und den aktiven Zustand.
 *
 * Sie kennt KEINE Varianten mehr — beim Wechsel tauscht `<nko-product-form>` das ganze
 * Element aus, das neue verbindet sich beim Einhaengen von selbst.
 * ==================================================================================== */

class NakedOpticsGallery extends HTMLElement {
  connectedCallback() {
    this.hoerer?.abort();
    this.hoerer = new AbortController();

    this.rail = this.querySelector("[data-nko-gallery-rail]") || this;
    this.slides = Array.from(this.querySelectorAll("[data-nko-slide]"));
    this.thumbs = Array.from(this.querySelectorAll("[data-nko-thumb]"));
    this.dots = Array.from(this.querySelectorAll("[data-nko-dot]"));

    if (!this.slides.length) return;

    this.addEventListener("click", this.onClick, {
      signal: this.hoerer.signal,
    });
    this.observeSlides();
  }

  disconnectedCallback() {
    this.hoerer?.abort();
    this.hoerer = null;
    if (this.observer) this.observer.disconnect();
    this.observer = null;
    if (this.lightboxObserver) this.lightboxObserver.disconnect();
    this.lightboxObserver = null;
    // Die Hoerer der Lightbox hingen am selben Signal — beim naechsten Oeffnen
    // baut `ensureLightbox` sie neu.
    if (this.lightbox) {
      if (this.lightbox.open) this.lightbox.close();
      this.lightbox.remove();
      this.lightbox = null;
    }
  }

  onClick = (event) => {
    // Zoom zuerst: der Slide-Link liegt tiefer als Thumbs/Dots und wuerde sonst
    // durchfallen und das Bild im Tab oeffnen.
    const zoom = event.target.closest("[data-nko-zoom]");
    if (zoom && this.contains(zoom)) {
      if (this.getAttribute("data-zoom") === "false") return; // settings.product_zoom_enable

      // Kein <dialog>-Support: NICHT abfangen. Ein per `open`-Attribut geoeffneter
      // Dialog waere nicht modal — keine Fokusfalle, kein Esc, kein inerter
      // Hintergrund — saehe aber modal aus und wuerde als transparente Flaeche
      // ueber der Seite alle Klicks schlucken. Dann lieber der Link: der oeffnet
      // das grosse Bild, und genau das ist die dokumentierte Variante ohne JS.
      if (typeof HTMLDialogElement === "undefined") return;

      event.preventDefault();
      this.openLightbox(zoom);
      return;
    }

    const control = event.target.closest("[data-nko-thumb], [data-nko-dot]");
    if (!control || !this.contains(control)) return;

    event.preventDefault(); // ohne JS bleibt es ein Anker

    const mediaId = control.dataset.nkoMediaId;
    const slide = this.slides.find(
      (node) => node.dataset.nkoMediaId === mediaId,
    );
    if (!slide) return;

    this.scrollToSlide(slide);
    this.setActive(mediaId); // optimistisch, nicht auf den Observer warten
  };

  /* ---- Lightbox ----------------------------------------------------------------
   * Ersetzt Fancybox (Teil von plugins.min.js, 67 KB) durch ein natives <dialog>.
   * Fokusfalle, Esc-Schliessen und das Inertisieren des Hintergrunds kommen damit
   * vom Browser statt aus einer Bibliothek.
   *
   * Das <dialog> wird beim ERSTEN Oeffnen gebaut, nicht in Liquid gerendert: sonst
   * laege eine zweite Kopie aller Bilder im Dokument, und genau das Gewicht haben
   * wir gerade aus der Galerie entfernt. Es haengt IN <nko-gallery>, also raeumt der
   * Regionstausch beim Variantenwechsel es automatisch mit weg und das neue Element
   * baut sich beim ersten Klick sein eigenes.
   *
   * GEBLAETTERT WIRD DURCH SCROLLEN. Die Bilder stehen als Folien in einer
   * Scroll-Snap-Schiene wie in der Galerie selbst; Wischen ist damit natives
   * Scrollen, ohne Gestenerkennung. Vorher gab es EIN <img>, dessen Quelle die
   * Pfeile tauschten — auf dem Handy liess sich nicht wischen, obwohl das CSS die
   * Pfeile dort genau deshalb an den Rand schiebt. Pfeile und Pfeiltasten
   * scrollen die Schiene, ein IntersectionObserver liest mit, welche Folie steht.
   * ---------------------------------------------------------------------------- */

  get zoomLinks() {
    return Array.from(this.querySelectorAll("[data-nko-zoom]"));
  }

  openLightbox(link) {
    const index = this.zoomLinks.indexOf(link);
    if (index < 0) return;

    const dialog = this.ensureLightbox();
    // Erst oeffnen, dann zum Bild springen: im geschlossenen Dialog
    // (`display: none`) hat die Schiene keine Breite, `scrollTo` liefe ins Leere.
    // Beides im selben Durchlauf, zu sehen ist nur das Ergebnis.
    dialog.showModal();
    this.showLightboxImage(index, true);
  }

  ensureLightbox() {
    if (this.lightbox && this.lightbox.isConnected) return this.lightbox;

    const label = (name, fallback) =>
      this.getAttribute(`data-label-${name}`) || fallback;
    const links = this.zoomLinks;

    // `tabindex="-1"` an der Schiene: Chrome nimmt scrollbare Bereiche ohne
    // fokussierbaren Inhalt sonst in die Tab-Folge — ein Halt ohne sichtbaren
    // Fokusring, weil der Dialog ueberstehende Umrisse abschneidet. Die
    // Pfeiltasten faengt `onLightboxKey` am Dialog ab.
    const dialog = document.createElement("dialog");
    dialog.className = "gallery__lightbox";
    dialog.innerHTML = `
      <button type="button" class="gallery__lightbox-close" data-nko-lightbox-close>
        <span class="visually-hidden"></span>
      </button>
      <button type="button" class="gallery__lightbox-nav gallery__lightbox-nav--prev" data-nko-lightbox-step="-1">
        <span class="visually-hidden"></span>
      </button>
      <div class="gallery__lightbox-rail" data-nko-lightbox-rail tabindex="-1">
        ${links.map(() => '<div class="gallery__lightbox-slide"><img class="gallery__lightbox-image" alt="" decoding="async"></div>').join("")}
      </div>
      <button type="button" class="gallery__lightbox-nav gallery__lightbox-nav--next" data-nko-lightbox-step="1">
        <span class="visually-hidden"></span>
      </button>
      <p class="gallery__lightbox-counter" data-nko-lightbox-counter></p>
    `;

    // Beschriftungen als Text setzen, nicht per innerHTML — sie kommen aus Liquid
    // und koennen Anfuehrungszeichen oder Klammern enthalten ("Schliessen (Esc)").
    // `aria-label` statt nur Text im Span. Der Schliessen-Button traegt sein
    // Symbol als CSS-`content`, und generierter Text zaehlt laut Spezifikation in
    // den zugaenglichen Namen hinein — ohne aria-label hiess der Button
    // "mal Schliessen (Esc)". Ein gesetzter Name schneidet das ab.
    const benenne = (selektor, name) => {
      const el = dialog.querySelector(selektor);
      el.setAttribute("aria-label", name);
      el.querySelector("span").textContent = name;
    };
    benenne("[data-nko-lightbox-close]", label("close", "Close"));
    benenne('[data-nko-lightbox-step="-1"]', label("prev", "Previous"));
    benenne('[data-nko-lightbox-step="1"]', label("next", "Next"));

    // Der Dialog selbst braucht einen Namen — `showModal()` liefert Rolle und
    // Modalitaet, aber keinen Namen. Ohne ihn hoert man beim Oeffnen nur
    // "Dialog, Schliessen, Schaltflaeche" und erfaehrt nicht, was sich geoeffnet hat.
    const galerieName = this.getAttribute("data-label-gallery");
    if (galerieName) dialog.setAttribute("aria-label", galerieName);

    // Der Zaehler ist die einzige Rueckmeldung beim Blaettern. Ohne Live-Region
    // aendert sich sein Text, ohne dass ein Screenreader etwas sagt (WCAG 4.1.3).
    const zaehler = dialog.querySelector("[data-nko-lightbox-counter]");
    zaehler.setAttribute("role", "status");
    zaehler.setAttribute("aria-live", "polite");
    zaehler.setAttribute("aria-atomic", "true");

    // Bei nur einem Bild sind Pfeile und Zaehler sinnlos. Die Bildmenge steht
    // fest, solange dieser Dialog lebt (siehe oben: neuer Satz, neues Element).
    for (const nav of dialog.querySelectorAll("[data-nko-lightbox-step]"))
      nav.hidden = links.length < 2;
    zaehler.hidden = links.length < 2;

    const signal = this.hoerer ? this.hoerer.signal : undefined;
    dialog.addEventListener("click", this.onLightboxClick, { signal });
    dialog.addEventListener("keydown", this.onLightboxKey, { signal });

    // Welche Folie steht? Nach dem Wischen weiss das nur die Schiene. Die Folien
    // sind so breit wie sie, ueber 60 % sichtbar ist also immer genau eine.
    // `intersectionRatio` statt `isIntersecting`: das ist je nach Browser schon
    // wahr, sobald die Nachbarfolie den Rand beruehrt.
    const schiene = dialog.querySelector("[data-nko-lightbox-rail]");
    const folien = Array.from(schiene.children);
    if (typeof IntersectionObserver === "function") {
      this.lightboxObserver?.disconnect();
      this.lightboxObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.intersectionRatio < 0.6) continue;
            const steht = folien.indexOf(entry.target);
            // Laeuft die Schiene gerade zu einem Ziel der Pfeile, zaehlen die
            // Folien unterwegs nicht: sonst setzt die Meldung der DURCHLAUFENEN
            // Folie den Stand zurueck, und der naechste schnelle Klick rechnet
            // von dort — drei Klicks kamen als zwei an (Chromium, 150 ms Takt).
            if (this.lightboxZiel != null && steht !== this.lightboxZiel)
              continue;
            this.lightboxZiel = null;
            this.zeigeLightboxStand(steht);
          }
        },
        { root: schiene, threshold: 0.6 },
      );
      for (const folie of folien) this.lightboxObserver.observe(folie);
    }

    // Ein Finger auf der Schiene uebernimmt: ab da gilt wieder, was steht.
    // (Bricht ein Wisch das weiche Scrollen ab, kaeme das Ziel sonst nie an und
    // der Observer bliebe stumm.)
    schiene.addEventListener(
      "pointerdown",
      () => {
        this.lightboxZiel = null;
      },
      { signal, passive: true },
    );

    // Mit zwei Fingern herangezoomt soll der Finger den Ausschnitt verschieben,
    // nicht zum naechsten Bild wischen: solange haelt das CSS die Schiene fest.
    const sicht = window.visualViewport;
    if (sicht) {
      sicht.addEventListener(
        "resize",
        () => {
          const gezoomt = sicht.scale > 1.01;
          if (gezoomt === dialog.hasAttribute("data-gezoomt")) return;
          dialog.toggleAttribute("data-gezoomt", gezoomt);
          // Beim Wechsel gilt, was STEHT. Setzt der Zoom mitten im weichen
          // Scrollen ein, haelt das CSS die Schiene an: das Ziel der Pfeile
          // kommt nie an, der Zaehler zeigte weiter das Ziel („4 / 4“ bei
          // Bild 2, Nachpruefung 06.10.2026). Also Ziel verwerfen und den
          // Stand von der Schiene lesen.
          this.lightboxZiel = null;
          if (schiene.clientWidth > 0)
            this.zeigeLightboxStand(
              Math.round(schiene.scrollLeft / schiene.clientWidth),
            );
        },
        { signal },
      );
    }

    // KEINE eigene Fokusrueckgabe: `showModal()` merkt sich das zuvor fokussierte
    // Element und stellt es beim Schliessen selbst wieder her — das ist im Standard
    // so festgelegt. Ein manuelles `focus()` konkurriert damit nur. Gemessen hat es
    // hier ohnehin nichts bewirkt, weil das Consent-Widget (PANDECTES-CMP) den Fokus
    // an sich zieht; dagegen anzuprogrammieren waere die falsche Antwort.

    this.appendChild(dialog);
    this.lightbox = dialog;
    return dialog;
  }

  /** Die Schiene zur Folie `index` bringen (Pfeile, Pfeiltasten, Oeffnen). */
  showLightboxImage(index, sofort = false) {
    const links = this.zoomLinks;
    if (!links.length) return;
    // Herangezoomt haelt das CSS die Schiene fest (der Finger verschiebt den
    // Ausschnitt). Pfeile und Pfeiltasten zaehlten trotzdem weiter, das Bild
    // blieb stehen — dann tun sie lieber nichts, bis wieder herausgezoomt ist.
    if (!sofort && this.lightbox.hasAttribute("data-gezoomt")) return;

    // Umlaufend blaettern, wie Fancybox es tat.
    const wrapped = ((index % links.length) + links.length) % links.length;
    const schiene = this.lightbox.querySelector("[data-nko-lightbox-rail]");
    const folie = schiene.children[wrapped];
    if (!folie) return;

    // Weich nur zur Nachbarfolie. Beim Oeffnen und beim Umlauf (letztes Bild ->
    // erstes) springt die Schiene, sonst liefe sie sichtbar durch alle Bilder
    // dazwischen.
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const weich =
      !sofort && !reduced && Math.abs(wrapped - this.lightboxIndex) === 1;

    // Das Ziel merken, bis die Schiene dort steht (siehe Observer). Ohne
    // IntersectionObserver gibt es niemanden, der es wieder loescht — dann gar
    // nicht erst setzen.
    this.lightboxZiel = this.lightboxObserver ? wrapped : null;
    this.zeigeLightboxStand(wrapped); // optimistisch, nicht auf den Observer warten
    schiene.scrollTo({
      left: folie.offsetLeft,
      behavior: weich ? "smooth" : "auto",
    });
  }

  /** Alles, was der Folie folgt: Bilder laden, Zaehler, Galerie dahinter. */
  zeigeLightboxStand(index) {
    const links = this.zoomLinks;
    if (index < 0 || index >= links.length) return;
    this.lightboxIndex = index;

    // Die Nachbarn gleich mit, damit beim Wischen kein leerer Platz hereinfaehrt.
    for (const i of [index, index + 1, index - 1])
      this.ladeLightboxBild((i + links.length) % links.length);

    const counter = this.lightbox.querySelector("[data-nko-lightbox-counter]");
    if (counter) counter.textContent = `${index + 1} / ${links.length}`;

    // Die Galerie mitfuehren, damit nach dem Schliessen dasselbe Bild sichtbar ist.
    // NUR waagrecht und nur die Schiene: `scrollToSlide` (scrollIntoView mit
    // `block: 'nearest'`) scrollt auch die SEITE, sobald das Galeriebild oben
    // angeschnitten ist — sie sprang beim Oeffnen der Lightbox um bis zu 270px
    // und stand nach dem Schliessen an anderer Stelle (iOS-Test 06.10.2026).
    // Ohne Animation: die Galerie liegt hinter dem Dialog, niemand sieht zu.
    const slide = this.slides[index];
    if (slide) {
      this.rail.scrollLeft +=
        slide.getBoundingClientRect().left -
        this.rail.getBoundingClientRect().left;
      this.setActive(slide.dataset.nkoMediaId);
    }
  }

  /** Die Folie `index` bekommt ihr Bild — einmal, beim ersten Bedarf. */
  ladeLightboxBild(index) {
    const link = this.zoomLinks[index];
    const img = this.lightbox.querySelectorAll(".gallery__lightbox-image")[
      index
    ];
    if (!link || !img || img.hasAttribute("src")) return;
    const slideBild = link.querySelector("img");

    // Erst das bereits dekodierte Slide-Bild zeigen, dann auf die grosse Fassung
    // wechseln. Sonst steht die Lightbox schwarz, solange das grosse Bild laedt —
    // auf dem Desktop ist das immer ein Kaltstart, weil `sizes` dort bei 496px
    // deckelt und die 1600er Fassung noch nie geholt wurde.
    const gross = link.getAttribute("href");
    const warm = slideBild && slideBild.currentSrc;
    img.alt = slideBild ? slideBild.alt : "";
    img.src = warm || gross;

    if (warm && warm !== gross) {
      const hoch = new Image();
      hoch.src = gross;
      // Grosszuegige Frist: jede Folie laedt nur einmal, ein verpasster Wechsel
      // bliebe bei der kleinen Fassung stehen.
      decodeImage(hoch, 20000).then(() => {
        if (hoch.naturalWidth > 0) img.src = gross;
      });
    }
  }

  onLightboxClick = (event) => {
    const step = event.target.closest("[data-nko-lightbox-step]");
    if (step) {
      this.showLightboxImage(
        this.lightboxIndex + Number(step.dataset.nkoLightboxStep),
      );
      return;
    }
    if (event.target.closest("[data-nko-lightbox-close]")) {
      this.lightbox.close();
      return;
    }
    // Klick neben das Bild schliesst. Schiene und Folien fuellen den Dialog und
    // sind selbst unsichtbar — ein Treffer auf sie (nicht auf Bild, Zaehler oder
    // Knopf) heisst also: daneben geklickt.
    if (
      event.target === this.lightbox ||
      event.target.matches(".gallery__lightbox-rail, .gallery__lightbox-slide")
    ) {
      this.lightbox.close();
    }
  };

  onLightboxKey = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.showLightboxImage(this.lightboxIndex + 1);
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.showLightboxImage(this.lightboxIndex - 1);
    }
    // Esc uebernimmt das <dialog> selbst.
  };

  scrollToSlide(slide) {
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    slide.scrollIntoView({
      behavior: reduced ? "auto" : "smooth",
      block: "nearest", // niemals die Seite vertikal scrollen
      inline: "start",
    });
  }

  observeSlides() {
    if (typeof IntersectionObserver !== "function") return;

    this.observer = new IntersectionObserver(
      (entries) => {
        let best = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (!best || entry.intersectionRatio > best.intersectionRatio)
            best = entry;
        }
        if (best) this.setActive(best.target.dataset.nkoMediaId);
      },
      {
        root: this.rail === this ? null : this.rail,
        threshold: [0.5, 0.75, 1],
      },
    );

    for (const slide of this.slides) this.observer.observe(slide);
  }

  setActive(mediaId) {
    if (!mediaId || this.activeMediaId === mediaId) return;
    this.activeMediaId = mediaId;

    for (const list of [this.thumbs, this.dots]) {
      for (const node of list) {
        const isActive = node.dataset.nkoMediaId === mediaId;
        node.classList.toggle("is-selected", isActive);
        if (isActive) node.setAttribute("aria-current", "true");
        else node.removeAttribute("aria-current");
      }
    }
  }
}

/* ==================================================================================== */

if (!customElements.get("nko-product-form")) {
  customElements.define("nko-product-form", NakedOpticsProductForm);
}

if (!customElements.get("nko-gallery")) {
  customElements.define("nko-gallery", NakedOpticsGallery);
}

/* ----------------------------------------------------------------------------
   NUR ALS MODUL LADEN: <script src="…" type="module">, nie mit `defer`.
   Der Modulbereich haelt die Namen oben lokal. Module laufen wie `defer` nach
   dem Parsen in Dokumentreihenfolge — das ist Teil des Vertrags mit
   assets/face-scan-property.js (siehe addToCartIntern) —, aber nur einmal je URL
   und im strict mode.
   ---------------------------------------------------------------------------- */
